// web/src/tiferet/voice.ts — הקול של תפארת (29.09 · נכתב מחדש 30.09 אחה"צ).
// 🔑 כל משפט בסיור מוקלט מראש (השרת מקליט פעם אחת ושומר) — כאן רק מנגנים. ⛔ עלות לכל צפייה.
// 🔑 🔇 נשמר בדפדפן (העדפה של המכשיר). כל כשל = שקט — הטקסט תמיד כתוב על המסך.
//
// 🆕 30.09 — ביקורת ערכת העוזרת (13-ios-audio · 06-first-intro), לפי המימוש שנבדק בפלטפורמה לאמניות (gdUnlock):
//  (1) נגן <audio> אחד לכל החיים של הדף — באייפון רק נגן ש"נפתח" בנגיעה מורשה לנגן אחר כך בלי נגיעה.
//  (2) פתיחה ב-touchend / click / keydown (⛔ pointerdown — באייפון הוא לא נחשב נגיעה שמתירה קול), ובתוך הנגיעה עצמה,
//      בלי await לפניה: משפט שממתין מתנגן, ואם אין — WAV שקט על אותו נגן.
//  (3) unlocked נקבע רק כשניגון באמת הצליח.
//  (4) כל ניגון מחזיר איך הוא נגמר: "ended" (נשמע עד הסוף) · "blocked" (הדפדפן חסם) · "failed" · "stopped" · "muted".
//      ⇒ ההצגה העצמית מסומנת "נשמעה" רק על "ended" (lastEnded), ⛔ על מעבר של speaking() ל-null.
//  (5) תשובה ארוכה נחתכת לקטעים (עד ~600 תווים, בסוף משפט) ומתנגנת ברצף, עם הורדה מראש של הקטע הבא.
//      ⛔ עוד חיתוך שקט ב-700 תווים.
import { fetchBlob, postBlob } from "../api/client";

export type Result = "ended" | "blocked" | "failed" | "stopped" | "muted";

const MUTE_KEY = "atida.tiferet.muted";
const CHUNK = 600;                                          // השרת מקבל עד 700 לבקשה (/staff/tiferet/say)
const urls = new Map<string, Promise<string | null>>();
const said = new Map<string, Promise<string | null>>();
const subs = new Set<() => void>();

const el: HTMLAudioElement | null = typeof window !== "undefined" ? new Audio() : null;
if (el) { el.preload = "auto"; el.setAttribute("playsinline", ""); }

let unlocked = false;
let playing: string | null = null;                          // המפתח שמתנגן (או נטען) עכשיו
let endedKey: string | null = null;                         // המפתח האחרון שנשמע עד הסוף
let pending: { key: string; url: string } | null = null;    // נחסם ⇒ יתנגן בנגיעה הבאה
let seq = 0;                                                // כל ניגון מקבל מספר; קריאה ישנה מתעלמת
let cur: ((r: Result) => void) | null = null;               // מסיים את הניגון הנוכחי (עצירה)

function emit() { subs.forEach((f) => f()); }
export function subscribe(f: () => void) { subs.add(f); return () => { subs.delete(f); }; }

export const waiting = () => pending?.key ?? null;
export const canAutoplay = () => unlocked;
export const speaking = () => playing;
export const lastEnded = () => endedKey;

export function isMuted(): boolean {
  try { return window.localStorage.getItem(MUTE_KEY) === "1"; } catch { return false; }
}
export function setMuted(v: boolean) {
  try { if (v) window.localStorage.setItem(MUTE_KEY, "1"); else window.localStorage.removeItem(MUTE_KEY); } catch { /* חסום */ }
  if (v) stop();
  emit();
}

// ── WAV שקט (800 דגימות) — "פותח" את הנגן בנגיעה הראשונה ──
let silentUrl: string | null = null;
function silent(): string {
  if (silentUrl) return silentUrl;
  const n = 800, b = new ArrayBuffer(44 + n * 2), v = new DataView(b);
  const w = (o: number, s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  w(0, "RIFF"); v.setUint32(4, 36 + n * 2, true); w(8, "WAVE"); w(12, "fmt "); v.setUint32(16, 16, true);
  v.setUint16(20, 1, true); v.setUint16(22, 1, true); v.setUint32(24, 8000, true); v.setUint32(28, 16000, true);
  v.setUint16(32, 2, true); v.setUint16(34, 16, true); w(36, "data"); v.setUint32(40, n * 2, true);
  silentUrl = URL.createObjectURL(new Blob([b], { type: "audio/wav" }));
  return silentUrl;
}

/** מנגן כתובת אחת על הנגן. ⚠️ el.play() נקרא בתוך ה-executor — סינכרוני, ולכן גם בתוך נגיעה. */
function playUrl(my: number, url: string): Promise<Result> {
  return new Promise<Result>((resolve) => {
    if (!el) { resolve("failed"); return; }
    let settled = false;
    const end = (r: Result) => { if (settled) return; settled = true; if (cur === end) cur = null; el.onended = null; el.onerror = null; resolve(r); };
    cur = end;
    el.onended = () => end(my === seq ? "ended" : "stopped");
    el.onerror = () => end("failed");
    el.src = url;
    const pr = el.play();
    if (pr && typeof pr.then === "function") {
      pr.then(() => { unlocked = true; }).catch((e: unknown) => {
        end(e instanceof DOMException && e.name === "NotAllowedError" ? "blocked" : "failed");
      });
    } else unlocked = true;
  });
}

function finish(my: number, key: string, r: Result) {
  if (my !== seq) return;
  if (r === "ended") endedKey = key;
  playing = null; emit();
}

/** ניגון של קטע אחד או יותר ברצף (תשובה ארוכה). הקטע הבא נטען בזמן שהנוכחי מתנגן. */
async function run(key: string, getters: Array<() => Promise<string | null>>): Promise<Result> {
  stop();
  if (isMuted() || !el || !getters.length) return "muted";
  const my = ++seq;
  playing = key; emit();
  let next = getters[0]!();
  for (let i = 0; i < getters.length; i++) {
    const url = await next;
    if (my !== seq) return "stopped";
    if (i + 1 < getters.length) next = getters[i + 1]!();
    if (!url) { finish(my, key, "failed"); return "failed"; }
    const r = await playUrl(my, url);
    if (my !== seq) return "stopped";
    if (r !== "ended") {
      if (r === "blocked" && i === 0) pending = { key, url };   // ⇒ יתנגן בנגיעה הבאה
      finish(my, key, r);
      return r;
    }
  }
  finish(my, key, "ended");
  return "ended";
}

// ── הנגיעה: משפט שממתין מתנגן בתוך הנגיעה; אחרת — WAV שקט שפותח את הנגן ──
function gesture() {
  if (!el) return;
  if (pending) {
    const p = pending;
    pending = null;
    if (isMuted()) { emit(); return; }
    stop();
    const my = ++seq;
    playing = p.key; emit();
    void playUrl(my, p.url).then((r) => finish(my, p.key, r));
    return;
  }
  if (unlocked || playing) return;
  const u = silent();
  el.src = u;
  const pr = el.play();
  if (pr && typeof pr.then === "function") pr.then(() => { unlocked = true; if (el.src === u) el.pause(); }).catch(() => { /* ננסה בנגיעה הבאה */ });
}
if (typeof window !== "undefined") {
  for (const ev of ["touchend", "click", "keydown"]) window.addEventListener(ev, gesture, { capture: true, passive: true });
}

/** מוריד את ההקלטה פעם אחת ושומר כתובת מקומית. גם לטעינה מוקדמת של השלב הבא. */
export function load(hash: string | null | undefined): Promise<string | null> {
  if (!hash) return Promise.resolve(null);
  let p = urls.get(hash);
  if (!p) {
    p = fetchBlob(`/staff/tiferet/voice/${hash}`).then((b) => (b && b.size > 500 ? URL.createObjectURL(b) : null));
    void p.then((u) => { if (!u) urls.delete(hash); });     // כשל ⇒ ננסה שוב בפעם הבאה
    urls.set(hash, p);
  }
  return p;
}

/** מנגן משפט מוקלט. ⛔ מנגן כשמושתק. */
export function play(hash: string | null | undefined): Promise<Result> {
  if (!hash) { stop(); return Promise.resolve("failed"); }
  return run(hash, [() => load(hash)]);
}

// 📄 מוטי 29.09: "הפנייה לעתידה חייבת להתנגן מיד כשהיא נכנסת לאתר בפעם הראשונה."
// 🔴 הדפדפן חוסם קול לפני נגיעה ראשונה בדף — ⛔ אפשר לעקוף. ⇒ מנסים מיד; נחסם ⇒ המשפט "ממתין" ומתנגן בנגיעה הבאה
//    בכל מקום בדף (⛔ צריך למצוא כפתור). ההקלטה כבר הורדה ⇒ הניגון בנגיעה מיידי.
export function playWhenAllowed(hash: string | null | undefined): Promise<Result> {
  if (!hash || isMuted()) return Promise.resolve("muted");
  return play(hash);                                        // run() שם את המשפט ב-pending אם נחסם
}

// ── תשובה בשיחה: מוקלטת בשרת בכל פעם (⛔ נשמרת שם), ונשמרת כאן עד סוף הביקור (🔈 שוב — בלי עלות) ──
export const sayKey = (text: string) => `say:${text}`;

/** חיתוך לקטעים להקראה: בסוף משפט, עד ~600 תווים. שורה בלי פיסוק מקבלת נקודה (עצירה בקול). */
export function chunks(text: string): string[] {
  const t = text.replace(/([^.!?…:;,\s])\s*\n+/g, "$1. ").replace(/\s+/g, " ").trim();
  if (!t) return [];
  const parts = t.match(/[^.!?…]+[.!?…]*\s*/g) ?? [t];
  const out: string[] = [];
  let c = "";
  for (const p of parts) {
    if (c && (c + p).length > CHUNK) { out.push(c.trim()); c = ""; }
    c += p;
    while (c.length > CHUNK + 50) { out.push(c.slice(0, CHUNK).trim()); c = c.slice(CHUNK); }
  }
  if (c.trim()) out.push(c.trim());
  return out;
}

export function say(text: string): Promise<Result> {
  const parts = chunks(text);
  if (!parts.length) return Promise.resolve("failed");
  return run(sayKey(text), parts.map((t) => () => {
    let p = said.get(t);
    if (!p) {
      p = postBlob("/staff/tiferet/say", { text: t }).then((b) => (b && b.size > 500 ? URL.createObjectURL(b) : null));
      void p.then((u) => { if (!u) said.delete(t); });
      said.set(t, p);
    }
    return p;
  }));
}

export function stop() {
  seq++;
  if (pending) pending = null;
  const c = cur; cur = null;
  if (el) { el.onended = null; el.onerror = null; el.pause(); }
  if (c) c("stopped");
  if (playing !== null) playing = null;
  emit();
}
